//
// Media Management
//
//
// Main class of this package is the MediaManager. It is supposed to be used as a singleton.
// Import the module with 
// > import MediaManager from './flux-mediamanager.js';

class Media 
{
    constructor({ 
        id = -1,
        contentID = undefined,
        fileName = undefined,
        url = undefined,
        requiresDirectURL = false,
        type = undefined,
        fileSize = 0
    } = {}) {
        Object.assign( this, { id, contentID, fileName, url, requiresDirectURL, type, fileSize } );
    }

    setID( id ) { this.id = id; return this; }
    getID() { return this.id; }

    setContentID( id ) { this.contentID = id; return this; }
    getContentID() { return this.contentID; }

    setFileName( name ) { this.fileName = name; return this; }
    getFileName() { return this.fileName; }

    setURL( url ) { this.url = url; return this; }
    getURL() { return this.url; }

    setRequiresDirectURL( value ) { this.requiresDirectURL = value; return this; }
    getRequiresDirectURL() { return this.requiresDirectURL; }

    setType( type ) { this.type = type; return this; }
    getType() { return this.type; }
    hasType( id ) { return this.type === id; }

    setFileSize( size ) { this.fileSize = size; return this; }
    getFileSize() { return this.fileSize; }

    // Parses this media type from the given JSON object
    parseFromJSON( json ) {
        // not implemented...
        if( json && json.idName && json.filename && json.contentId ) {
            // Seems to be a datatype from the CMS...
            this.setID( json.idName );
            this.setFileName( json.filename );
            this.setContentID( json.contentId );
            this.setFileSize( json.expected_file_size );
        } else {
            // Unknown datatype...
            console.error("Trying to parse unknown filetype for " + json);
        }
    }

    createFromJSON() {

    }

    toJSON() {
        return {
            id: this.id,
            contentID: this.contentID,
            fileName: this.fileName,
            url: this.url,
            fileSize: this.fileSize,
            type: this.type
        }
    }
}

Media.MediaType = Object.freeze({
    Texture: 1001,
    Video: 1002,
    VideoList: 30000,
    Audio: 1003,
    Text: 1004,
    Model3D: 0,
    WebsiteURL: 1008,
    PDF: 1009,
    Prefab: 30000
  })

class Texture extends Media
{
    get defaultTransitionTextureID() { return -1; }
    
    constructor({ 
        id = -1,
        fileName = undefined,
        url = undefined,
        isReadable = false,
        contentID = undefined,
        mipmapping = true,
        linearColorSpace = false,
        filterMode = "BiLinear",
        ansioLevel = 1,
        alphaIsTransparency = false,
        fileSize = 0,
        width = undefined,
        height = undefined,
        texture = undefined
    } = {}) {
        super( { id, fileName, url, contentID, fileSize });
        this.setType( Media.MediaType.Texture );
        Object.assign( this, { isReadable, mipmapping, linearColorSpace, filterMode, ansioLevel, alphaIsTransparency, width, height, texture } );
    }

    setIsReadable( value ) { this.isReadable = value; return this; }
    getIsReadable() { return this.isReadable; }

    setMipmapping( value ) { this.mipmapping = value; return this; }
    getMipmapping() { return this.mipmapping; }

    setLinearColorSpace( value ) { this.linearColorSpace = value; return this; }
    getLinearColorSpace() { return this.linearColorSpace; }

    setFilterMode( value ) { this.filterMode = value; return this; }
    getFilterMode() { return this.filterMode; }

    setAnsioLevel( value ) { this.ansioLevel = value; return this; }
    getAnsioLevel() { return this.ansioLevel; }

    setAlphaIsTransparency( value ) { this.alphaIsTransparency = value; return this; }
    getAlphaIsTransparency() { return this.alphaIsTransparency; }

    setTexture( texture ) { this.texture = texture; return this; }
    getTexture() { return this.texture; }

    setSize( width, height ) { this.width = width; this.height = height; return this; }
    setWidth( width ) { this.width = width; return this; }
    getWidth() { return this.width; }

    setHeight( height ) { this.height = height; return this; }
    getHeight() { return this.height; }

    parseFromJSON( json ) {
        // not implemented...
        if( json && json.type === Media.MediaType.Texture ) {
            super.parseFromJSON( json );

            // Seems to be a datatype from the CMS...
            this.setIsReadable( json.isReadable );
            this.setMipmapping( json.mipmapping );
            this.setLinearColorSpace( json.linearColorSpace );
            this.setFilterMode( json.filterMode );
            this.setAnsioLevel( json.ansioLevel );
            this.setAlphaIsTransparency( json.alphaIsTransparency );
            //this.setRequiresDirectURL( true );
        } else {
            // Unknown datatype...
            console.error("Trying to parse unknown filetype into texture");
        }
        return this;
    }

    createFromJSON( json ) {
        return new Texture().parseFromJSON( json );
    }

    toJSON() {
        var rv = super.toJSON();
        rv.isReadable = this.isReadable;
        rv.mipmapping = this.mipmapping;
        rv.linearColorSpace = this.linearColorSpace;
        rv.filterMode = this.filterMode;
        rv.ansioLevel = this.ansioLevel;
        rv.alphaIsTransparency = this.alphaIsTransparency; 
        rv.width = this.width;
        rv.height = this.height;
        return rv;
    }
}


class Audio extends Media
{
    constructor({ 
        id = -1,
        fileName = undefined,
        url = undefined,
        contentID = undefined,
        isThreeD = false,
        isStream = false,
        fileSize = 0
    } = {}) {
        super( { id, fileName, url, contentID, fileSize} );
        this.setType( Media.MediaType.Audio );
        Object.assign( this, { isThreeD, isStream } );
    }

    setIsThreeD( value ) { this.isThreeD = value; return this; }
    getIsThreed() { return this.isThreeD; }

    setStream( value ) { this.isStream = value; return this; }
    getStream() { return this.isStream; }    

    parseFromJSON( json ) {
        if( json && json.type === Media.MediaType.Audio ) {
            super.parseFromJSON( json );

            // Seems to be a datatype from the CMS...
            this.setIsThreeD( json.isThreeD );
            this.setStream( json.isStream );
            this.setRequiresDirectURL( true );
        } else {
            // Unknown datatype...
            console.error("Trying to parse unknown filetype");
        }
        return this;
    }

    createFromJSON( json ) {
        return new Audio().parseFromJSON( json );
    }
    
    toJSON() {
        var rv = super.toJSON();
        rv.isThreeD = this.isThreeD;
        rv.isStream = this.isStream;
        return rv;
    }
}

class Video extends Media
{
    constructor({ 
        id = -1,
        fileName = undefined,
        url = undefined,
        contentID = undefined,
        width = undefined,
        height = undefined,
        fileSize = 0
    } = {}) {
        super( { id, fileName, url, contentID, fileSize });
        this.setType( Media.MediaType.Video );
        Object.assign( this, { width, height } );
    }

    setSize( width, height ) { this.width = width; this.height = height; return this; }
    setWidth( width ) { this.width = width; return this; }
    getWidth() { return this.width; }

    setHeight( height ) { this.height = height; return this; }
    getHeight() { return this.height; }

    parseFromJSON( json ) {
        // not implemented...
        if( json && json.type === Media.MediaType.Video ) {
            super.parseFromJSON( json );

            // Seems to be a datatype from the CMS...
            this.setWidth( json.width );
            this.setHeight( json.height );
            this.setRequiresDirectURL( true );
        } else {
            // Unknown datatype...
            console.error("Trying to parse unknown filetype");
        }
        return this;
    }

    createFromJSON( json ) {
        return new Video().parseFromJSON( json );
    }
    

    toJSON() {
        var rv = super.toJSON();
        rv.width = this.width;
        rv.height = this.height;
        return rv;
    }
}

class VideoList extends Media
{
    constructor({ 
        id = -1,
        fileName = undefined,
        url = undefined,
        contentID = undefined,
        width = undefined,
        height = undefined,
        fileSize = 0
    } = {}) {
        super( { id, fileName, url, contentID, fileSize });
        this.setType( Media.MediaType.Video );
        Object.assign( this, { width, height } );
    }

    setSize( width, height ) { this.width = width; this.height = height; return this; }
    setWidth( width ) { this.width = width; return this; }
    getWidth() { return this.width; }

    setHeight( height ) { this.height = height; return this; }
    getHeight() { return this.height; }

    parseFromJSON( json ) {
        // not implemented...
        if( json && json.type === Media.MediaType.Video ) {
            super.parseFromJSON( json );

            // Seems to be a datatype from the CMS...
            this.setWidth( json.width );
            this.setHeight( json.height );
            this.setRequiresDirectURL( true );
        } else {
            // Unknown datatype...
            console.error("Trying to parse unknown filetype");
        }
        return this;
    }

    createFromJSON( json ) {
        return new Video().parseFromJSON( json );
    }
    
    toJSON() {
        var rv = super.toJSON();
        rv.width = this.width;
        rv.height = this.height;
        return rv;
    }
}

class Model3D extends Media
{
    constructor({ 
        id = -1,
        fileName = undefined,
        url = undefined,
        contentID = undefined,
        fileSize = 0
    } = {}) {
        super( { id, fileName, url, contentID, fileSize} );
        this.setType( Media.MediaType.Model3D );
        Object.assign( this, { } );
    }

    parseFromJSON( json ) {
        if( json && json.type === Media.MediaType.Model3D ) {
            super.parseFromJSON( json );

            // Seems to be a datatype from the CMS...
            this.setRequiresDirectURL( true );
        } else {
            // Unknown datatype...
            console.error("Trying to parse unknown filetype");
        }
        return this;
    }

    createFromJSON( json ) {
        return new Model3D().parseFromJSON( json );
    }
    
    toJSON() {
        var rv = super.toJSON();
        return rv;
    }
}


export { Media, Texture, Audio, Video ,Model3D, VideoList}
